import type { UserChangeRequestReason } from '@contracts/changeRequest/ChangeRequestContract';
import Check from '@mui/icons-material/Check';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import type { Order } from '@src/orders/domain/Order';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { DirtyFields } from './DirtyFields';
import { useCreateChangeRequest } from './useCreateChangeRequest';

const useSelectedReason = (): { selectedReason: UserChangeRequestReason } => {
  const params = useParams();
  return {
    selectedReason: params.reason as UserChangeRequestReason, // Change Request Page doesn't exist without this param
  };
};
export const CreateChangeRequestFooter = ({
  order,
  readFormValues,
  triggerValidation,
  readFormDirtyFields,
}: {
  order: Order;
  triggerValidation: () => Promise<boolean>;
  readFormValues: () => Record<string, unknown>;
  readFormDirtyFields: () => DirtyFields;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { selectedReason } = useSelectedReason();
  const { refreshOrder } = useRefreshOrder({ id: order.id });
  const { navigateToOrderDetails } = useNavigateToOrderDetails();

  const { createChangeRequest } = useCreateChangeRequest({
    orderId: order.id,
    reason: selectedReason,
    onSuccess: () => {
      refreshOrder();
      showToast({
        severity: ToastLevel.success,
        message: t('createChangeRequest.success'),
      });
      navigateToOrderDetails({ orderId: order.id });
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('createChangeRequest.error'),
      }),
  });

  const submit = async () => {
    const formIsValid = await triggerValidation();
    if (!formIsValid) {
      return;
    }

    const formValues = readFormValues();
    const dirtyFields = readFormDirtyFields();
    createChangeRequest({ formValues, dirtyFields });
  };

  return (
    <Stack
      zIndex={10}
      position="sticky"
      bottom="0"
      style={{ backgroundColor: theme.palette.surface.surface }}
      paddingBottom={theme.spacing['6']}
      marginX="40px"
    >
      <Divider style={{ marginBottom: theme.spacing['6'] }} />
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={submit} startIcon={<Check />} variant="contained">
          {t('submit')}
        </Button>
      </Stack>
    </Stack>
  );
};
